import { t } from "i18next";
import { useFormContext, useWatch } from "react-hook-form";

import { computationOptions } from "#app/computationOptions";
import { colors } from "#app/theme/v1/colors";
import { PredictionFeedbackButtons } from "#modules/feedback/view/PredictionFeedbackButtons";
import { useOptimizedMachineTimes } from "#modules/optimizations/view/useOptimizedMachineTimes";
import { useOptimizedMaterialCosts } from "#modules/optimizations/view/useOptimizedMaterialCosts";
import { QuoteRequestLineFormData } from "#modules/quoteRequestLines/types/QuoteRequestLineFormData";
import { useQuoteRequestLine } from "#modules/quoteRequestLines/view/useQuoteRequestLine";
import { useQuoteRequestLineActivePrediction } from "#modules/quoteRequestLines/view/useQuoteRequestLineActivePrediction";
import { CostCollapsable } from "#modules/quoteRequestLines/views/DetailsPanel/CostCollapsable";
import { Features } from "#modules/quoteRequestLines/views/DetailsPanel/ProcessSteps/Features/Features";
import { ProcessSteps } from "#modules/quoteRequestLines/views/DetailsPanel/ProcessSteps/ProcessSteps";
import { ProcessStepForTable } from "#modules/quoteRequestLines/views/DetailsPanel/ProcessSteps/ProcessStepsTable";
import { WeldLength } from "#modules/quoteRequestLines/views/DetailsPanel/ProcessSteps/WeldLength/WeldLength";
import { useAlertChip } from "#modules/quoteRequestLines/views/useAlertChip";
import { Group } from "#shared/view/components/Group";
import { Space } from "#shared/view/components/Space";
import {
  AssemblyWithChildren,
  computeMaterialPrice,
  computeProcessesPrice,
  isPartTypeCompatibleWithFeaturesDetection,
  QuoteRequestLineDto,
} from "shared";

export const ProcessStepsSection = ({
  assemblyChildren,
  hasChildren,
  processSteps,
}: {
  assemblyChildren: AssemblyWithChildren<QuoteRequestLineDto>[];
  hasChildren: boolean;
  processSteps: ProcessStepForTable[];
}) => {
  const { control } = useFormContext<QuoteRequestLineFormData>();

  const quoteRequestLineId = useWatch({ control, name: "quoteRequestLineId" });
  const quantity = useWatch({ control, name: "quantity" });
  const part = useWatch({ control, name: "part" });

  const { prediction } = useQuoteRequestLineActivePrediction(quoteRequestLineId);
  const { quoteRequestLine } = useQuoteRequestLine({ id: quoteRequestLineId });
  const { alerts } = useAlertChip(quoteRequestLine || undefined, assemblyChildren);
  const optimizedMachineTimes = useOptimizedMachineTimes(quoteRequestLine?.quoteRequestId);
  const optimizedMaterialCosts = useOptimizedMaterialCosts(quoteRequestLine?.quoteRequestId);

  if (!quoteRequestLine) {
    return null;
  }

  const processStepsPrice = computeProcessesPrice({
    quantity: quantity || 1,
    reworkRatio: quoteRequestLine.reworkRatio,
    quantityInAssembly: part?.quantityInAssembly || 1,
    addedValueMarginRatio: quoteRequestLine.addedValueMarginRatio,
    productsMarginRatio: quoteRequestLine.productsMarginRatio,
    processSteps: processSteps.map((processStep) => ({
      ...processStep,
      industrialProcess: processStep.industrialProcess,
    })),
    optimizedMachineTimes,
  });

  const shouldDisplayFeaturesSection =
    !hasChildren && part && isPartTypeCompatibleWithFeaturesDetection(part.type);

  const predictedAddedValuePrice = processStepsPrice.partProcessesCost;
  const predictedMaterialPrice = quoteRequestLine.layout
    ? computeMaterialPrice({ quoteRequestLine, optimizedMaterialCosts }, computationOptions)
        .partMaterialCost
    : undefined;
  const predictedTotalPrice =
    predictedMaterialPrice !== undefined
      ? predictedMaterialPrice + predictedAddedValuePrice
      : undefined;

  return (
    <CostCollapsable
      title={t("quoteRequestLine.addedValueCost")}
      color={colors.violet[5]}
      value={processStepsPrice.partProcessesCost}
      defaultOpened
      data-testid="process-steps-section"
    >
      {shouldDisplayFeaturesSection && (
        <Features
          partId={part.id}
          title={t("quoteRequestLine.detectedShapes")}
          partType={part.type}
          editable
        />
      )}

      {hasChildren && <WeldLength partId={part?.id} />}

      <ProcessSteps processSteps={processSteps} alerts={alerts} hasChildren={hasChildren} />

      <Space h="xs" />
      <Group justify="flex-end">
        {prediction?.id && (
          <PredictionFeedbackButtons
            predictionId={prediction.id}
            size="xs"
            predictedMaterialPrice={predictedMaterialPrice}
            predictedAddedValuePrice={predictedAddedValuePrice}
            predictedTotalPrice={predictedTotalPrice}
          />
        )}
      </Group>
    </CostCollapsable>
  );
};
